import { useSearchParams } from "react-router-dom";
import { useLocale, useT } from "../i18n";
import { useTaxonomies } from "../lib/taxonomies";
import { Seo } from "../components/Seo";
import { Area, Choice, CountryField, FormFooter, FormGroup, FormLayout, NextSteps, PageHero, Success, Text, check, toInt, useRequestForm } from "../components/forms/FormKit";

const P = "sourcing";

export default function SourcingPage() {
  const t = useT();
  const lang = useLocale();
  const [sp] = useSearchParams();  // prefill from "Find a Similar Vehicle" on sold detail pages
  const [tx] = useTaxonomies();
  const pre = (k) => (sp.get(k) || "").slice(0, 80);
  const ctx = useRequestForm({ make: pre("make"), model: pre("model"), year_from: pre("year_from"), year_to: pre("year_to"), body_type: pre("body_type"), budget_jpy: "", mileage_max: "",
    destination: ["japan", "export"].includes(sp.get("destination")) ? sp.get("destination") : lang === "ja" ? "japan" : "export",
    country: "", destination_port: "", name: "", email: "", phone: "", message: "", consent: false, website: "" });
  const { form } = ctx;
  const exporting = form.destination === "export";
  const years = [["", "—"], ...Array.from({ length: 40 }, (_, i) => String(new Date().getFullYear() - i)).map((y) => [y, y])];

  const submit = (e) => {
    e.preventDefault();
    const errs = check(form, { make: { req: true, max: 60 }, model: { max: 80 }, budget_jpy: { int: true }, mileage_max: { int: true },
      name: { req: true, max: 100 }, email: { req: true, email: true, max: 200 }, phone: { max: 40 }, message: { max: 3000 },
      ...(exporting ? { country: { req: true }, destination_port: { max: 100 } } : {}) }, t);
    if (form.year_from && form.year_to && +form.year_from > +form.year_to) errs.year_to = t("form.errors.yearOrder");
    ctx.send("/requests/sourcing", errs, (fill_ms) => ({
      locale: lang, make: form.make.trim(), model: form.model.trim(), year_from: toInt(form.year_from), year_to: toInt(form.year_to),
      budget_jpy: toInt(form.budget_jpy), mileage_max: toInt(form.mileage_max), body_type: form.body_type || null, destination: form.destination,
      country: exporting ? form.country : null, destination_port: exporting && form.destination_port.trim() ? form.destination_port.trim() : null,
      name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), message: form.message.trim(), consent: form.consent, website: form.website, fill_ms,
    }));
  };

  return (
    <div data-testid="sourcing-page">
      <Seo title={t("pages.sourcing.title")} description={t("pages.sourcing.description")} path="sourcing" />
      <PageHero eyebrow={t("sourcingPage.eyebrow")} title={t("sourcingPage.title")} intro={t("sourcingPage.intro")} />
      <FormLayout aside={<NextSteps label={t("pages.sourcing.title")} />}>
        {ctx.phase === "success" ? <Success prefix={P} onAgain={ctx.reset} /> : (
          <form onSubmit={submit} noValidate className="grid gap-10" data-testid="sourcing-form">
            <FormGroup title={t("sourcingPage.vehicle")}>
              <div className="grid gap-5 sm:grid-cols-2">
                <Text ctx={ctx} prefix={P} k="make" label={t("sourcingPage.make")} />
                <Text ctx={ctx} prefix={P} k="model" label={t("sourcingPage.model")} optional />
                <Choice ctx={ctx} prefix={P} k="year_from" label={t("sourcingPage.yearFrom")} options={years} optional />
                <Choice ctx={ctx} prefix={P} k="year_to" label={t("sourcingPage.yearTo")} options={years} optional />
                <Choice ctx={ctx} prefix={P} k="body_type" label={t("labels.bodyType")} options={[["", "—"], ...(tx?.body_types || []).map((b) => [b.key, b.label])]} optional />
                <Text ctx={ctx} prefix={P} k="budget_jpy" label={t("sourcingPage.budget")} inputMode="numeric" optional />
                <Text ctx={ctx} prefix={P} k="mileage_max" label={t("sourcingPage.mileageMax")} inputMode="numeric" optional />
              </div>
            </FormGroup>
            <FormGroup title={t("sourcingPage.delivery")}>
              <div role="radiogroup" aria-label={t("sourcingPage.destination")} className="grid grid-cols-2 border border-ink/20" data-testid="sourcing-destination">
                {["japan", "export"].map((d) => (
                  <button key={d} type="button" role="radio" aria-checked={form.destination === d} onClick={() => ctx.setForm((f) => ({ ...f, destination: d }))}
                    className={`min-h-[48px] px-4 text-sm transition-colors duration-300 ${form.destination === d ? "bg-ink text-white" : "bg-white text-ink/70 hover:text-ink"}`} data-testid={`sourcing-destination-${d}`}>
                    {t(`sourcingPage.${d}`)}
                  </button>
                ))}
              </div>
              {exporting && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <CountryField ctx={ctx} prefix={P} k="country" label={t("form.country")} exclude={["JP"]} />
                  <Text ctx={ctx} prefix={P} k="destination_port" label={t("form.port")} optional />
                </div>
              )}
            </FormGroup>
            <FormGroup title={t("form.yourDetails")}>
              <div className="grid gap-5 sm:grid-cols-2">
                <Text ctx={ctx} prefix={P} k="name" label={t("form.name")} autoComplete="name" className="sm:col-span-2" />
                <Text ctx={ctx} prefix={P} k="email" label={t("form.email")} type="email" autoComplete="email" inputMode="email" />
                <Text ctx={ctx} prefix={P} k="phone" label={t("form.phone")} type="tel" autoComplete="tel" optional />
              </div>
              <Area ctx={ctx} prefix={P} k="message" label={t("form.message")} placeholder={t("sourcingPage.messagePlaceholder")} optional />
            </FormGroup>
            <FormFooter ctx={ctx} prefix={P} submitLabel={t("sourcingPage.submit")} />
          </form>
        )}
      </FormLayout>
    </div>
  );
}
